import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { css } from '@emotion/core'
import { useLazyQuery } from '@apollo/client/react'
import { encodeArk } from '@fs/zion-ark'
import { baseUrl } from '@fs/zion-config'
import { MenuNewWindow } from '@fs/zion-icon'
import { useLocation, useQueryParams } from '@fs/zion-router'
import { usePermission } from '@fs/zion-permissions'
import { colors, Button, Bleed, H3, Subheading } from '@fs/zion-ui'
import queries from '../../queries'
import { getGroupPermissions, getImagePermission } from '../../../../../shared/services/das/dasService'
import transl from './Internal.transl'
import { ListItemGroupData, LoadingWrapper } from '../../components'
import QualityTags from './components/qualityTags/QualityTags'
import { useGroupData, useImageOrganizeModes, useImageOrganizeContext, useDetermineMode } from '../../hooks'
import ReprocessFolder from './components/reprocessFolder/ReprocessFolder'
import ReworkFolder from './components/reworkFolder/ReworkFolder'
import DivideModeButton from './components/divideModeButton/DivideModeButton'
import Watermarking from './components/watermarking/Watermarking'

const internalCss = css`
  background: ${colors.gray02};
  padding: 8px 16px;
`

function linkToSearch(key, value, altText) {
  if (!key || !value) return []
  return [
    {
      text: altText || value,
      to: `/records/images/search-results?${key}=${value}`,
    },
  ]
}

// Display QualityTags component if any qTag or comment is available in the properties
function qualityTagsAndCommentsExist(properties) {
  const folderQTags = properties.find((p) => p?.name === 'folderQTags' && p?.type === 'GROUP')
  const comments = properties.find((p) => p?.name === 'comments')
  const recordCaptureComment = properties.find((p) => p?.name === 'recordCaptureComment')

  if (folderQTags || comments || recordCaptureComment) {
    return <QualityTags comments={comments} folderQTags={folderQTags} recordCaptureComment={recordCaptureComment} />
  }

  return null
}

export default function Internal() {
  const location = useLocation()
  const { query } = useQueryParams()

  const [reworkAuthorized, reworkAuthorizedStatus] = usePermission(
    'LightyearApproveReworkPermission',
    'LightyearContext'
  )
  const [hasWatermarkingPermission] = usePermission('LightyearWatermarkingPermission', 'LightyearContext')

  const { hasEditPermissions } = useImageOrganizeContext()
  const {
    group: { internalFields = {}, naturalGroupId = null, imageApids, imageGroupNumber = null },
    itemCount = 0,
    initialGroup = {},
    loading,
    initialChildId,
  } = useGroupData()

  const hideGroupPermissions = itemCount !== 1 // Note: Do not fetch group permissions if it is a split group (itemCount  > 1), this is not yet supported

  const imageArks = useMemo(() => {
    if (imageApids?.length < 1) return []
    return imageApids?.reduce((acc, current) => {
      const ark = encodeArk(current)
      acc[ark] = current
      return acc
    }, {})
  }, [imageApids])

  const { internalData, properties = [] } = internalFields
  const isVolumeSet = initialGroup?.types?.includes('VOLUME_SET')
  const { isImageOrganizeMode } = useImageOrganizeModes()

  const { showMetadataAudit } = useDetermineMode()
  const currentArk = location?.pathname?.slice(12) || null

  const [getNineFive, { data: nineFive, loading: loadNineFive }] = useLazyQuery(queries.getArtifactNineFive())

  const currentApid = useMemo(() => {
    if (currentArk && Object.keys(imageArks || {}).length > 0) return imageArks?.[currentArk]
    return null
  }, [currentArk, imageArks])

  useEffect(() => {
    if (currentApid) {
      getNineFive({ variables: { imageApid: currentApid } })
    }
  }, [currentApid, getNineFive])

  const [imagePermissions, setImagePermissions] = useState(null)
  useEffect(() => {
    if (!currentArk) {
      setImagePermissions([])
      return
    }

    async function fetchImagePermissions(ark) {
      try {
        const res = await getImagePermission(ark)
        let permissions = res.data || ''
        permissions = permissions?.split(':') || []
        setImagePermissions(permissions)
      } catch (error) {
        console.error('Error fetching image permissions:', error)
        setImagePermissions([])
      }
    }
    fetchImagePermissions(currentArk)
  }, [currentArk])

  const [groupPermissions, setGroupPermissions] = useState(null)

  useEffect(() => {
    if (!imageGroupNumber || hideGroupPermissions) {
      setGroupPermissions([])
      return
    }

    async function fetchGroupPermissions(id) {
      try {
        const res = await getGroupPermissions(id)
        let permissions = res.data || ''
        permissions = permissions?.split(':') || []
        setGroupPermissions(permissions)
      } catch (error) {
        console.error('Error fetching group permissions:', error)
        setGroupPermissions([])
      }
    }
    fetchGroupPermissions(imageGroupNumber)
  }, [imageGroupNumber, hideGroupPermissions])

  const historyURL = useMemo(() => {
    let id = query.groupId
    if (internalData?.id) id = internalData.id

    return `${baseUrl}/records/images/history/${id}`
  }, [internalData, query])

  const projectIdLink = linkToSearch('projectId', internalData?.phoenixAcquisitionIds)
  const naturalGroupLink = linkToSearch('naturalGroupIds', naturalGroupId)
  const cameraOperatorLink = linkToSearch(
    'cameraOperatorId',
    internalData?.cameraOperatorNumber,
    internalData?.cameraOperator
  )

  const ManageJiapuGroupButton = useCallback(() => {
    const { volumeSetSearch, groupId: queryGroup } = query
    const display = isVolumeSet && !isImageOrganizeMode && volumeSetSearch && queryGroup
    const url = `/records/images/organize/${volumeSetSearch}/${queryGroup}`

    return display ? (
      <Button external Icon={MenuNewWindow} target="_blank" to={url}>
        {transl.manageJiapuGroup()}
      </Button>
    ) : null
  }, [isImageOrganizeMode, isVolumeSet, query])

  return (
    <Bleed sides>
      <div css={internalCss}>
        <LoadingWrapper loading={loading}>
          <H3 size="H5" skeletontype="heading" supplementary>
            {transl.heading()}
            <Subheading>{transl.subheading()}</Subheading>
          </H3>
        </LoadingWrapper>
        <LoadingWrapper loading={loading}>
          <ListItemGroupData overline={transl.projectId()} hyperlinks={projectIdLink} />
          <ListItemGroupData overline={transl.mediaNumber()} items={internalData?.originalMediaNumber} />
          <ListItemGroupData overline={transl.naturalGroupIds()} hyperlinks={naturalGroupLink} />
          <ListItemGroupData overline={transl.cameraOperator()} hyperlinks={cameraOperatorLink} />
          <ListItemGroupData overline={transl.imageApid()} hyperlinks={linkToSearch('apid', currentApid)} />
          <ListItemGroupData overline={transl.imageArk()} hyperlinks={linkToSearch('ark', currentArk)} />
          <ListItemGroupData
            overline={transl.imageNineFive()}
            items={loadNineFive ? ['-'] : [nineFive?.getArtifactNineFive].filter(Boolean)}
          />
          <ListItemGroupData
            overline={transl.imagePermissions()}
            hasMoreLess={imagePermissions?.length > 4}
            items={imagePermissions}
          />
          {!hideGroupPermissions && (
            <ListItemGroupData
              overline={transl.groupPermissions()}
              hasMoreLess={groupPermissions?.length > 4}
              items={groupPermissions}
            />
          )}
        </LoadingWrapper>
        <LoadingWrapper loading={loading}>
          <Button Icon={MenuNewWindow} to={historyURL} target="_blank" skeletontype="button">
            {transl.openFullHistory()}
          </Button>
          <>
            {hasEditPermissions && !isImageOrganizeMode && !isVolumeSet && !showMetadataAudit && <DivideModeButton />}
            {qualityTagsAndCommentsExist(properties)}
            {reworkAuthorized && reworkAuthorizedStatus === 'loaded' && !isImageOrganizeMode ? (
              <ReprocessFolder skeletontype="expander" />
            ) : null}
            {!isImageOrganizeMode && <ReworkFolder skeletontype="expander" />}
            {hasWatermarkingPermission && (
              <Watermarking currentApid={currentApid} groupId={initialChildId} properties={properties} />
            )}
            <ManageJiapuGroupButton />
          </>
        </LoadingWrapper>
      </div>
    </Bleed>
  )
}
